import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import CollegeListView from '../components/CollegeListView';

const CollegeManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [editingCollege, setEditingCollege] = useState(null); // null = create mode
  
  const [collegeForm, setCollegeForm] = useState({
    name: '',
    authorized_contact_name: '',
    authorized_contact_email: '',
    province: '',
    city: '',
    programs: []
  });
  
  const [programForm, setProgramForm] = useState({
    program_name: '',
    tuition_fee: '',
    duration: '',
    currency: 'INR',
    college_to_tge_rate: '',
    college_to_tge_percent: '',
    tge_to_recruiter_rate: '',
    tge_to_recruiter_percent: '',
  });

  const [programError, setProgramError] = useState('');
  const [editingProgramIdx, setEditingProgramIdx] = useState(-1); // -1 means none
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  const [scrapeResults, setScrapeResults] = useState([]); // [{program_name, duration, _selected}]
  
  const [commissionForm, setCommissionForm] = useState({
    recruiter_id: '',
    program_name: '',
    commission_type: 'percentage',
    commission_value: ''
  });

  const [collegeCommissions, setCollegeCommissions] = useState([]);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [ratesCollege, setRatesCollege] = useState(null);

  const openRatesModal = async (college) => {
    setRatesCollege(college);
    setShowRatesModal(true);
    setCommissionForm({
      recruiter_id: '',
      program_name: college.programs?.[0]?.program_name || '',
      commission_type: 'flat',
      commission_value: '',
    });
    await loadCommissionsForCollege(college.id);
  };

  const loadCommissionsForCollege = async (collegeId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions?college_id=${encodeURIComponent(collegeId)}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCollegeCommissions(data.commissions || []);
      }
    } catch (_) {}
  };

  const submitCommission = async (e) => {
    e.preventDefault();
    if (!ratesCollege) return;
    if (!commissionForm.recruiter_id || !commissionForm.program_name || !commissionForm.commission_value) {
      alert('Pick a recruiter, program, and amount.');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          college_id: ratesCollege.id,
          program_name: commissionForm.program_name,
          recruiter_id: commissionForm.recruiter_id,
          commission_type: commissionForm.commission_type,
          commission_value: Number(commissionForm.commission_value),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      setCommissionForm({ ...commissionForm, recruiter_id: '', commission_value: '' });
      await loadCommissionsForCollege(ratesCollege.id);
    } catch (err) { alert(err.message); }
  };

  const removeCommission = async (id) => {
    if (!window.confirm('Remove this recruiter commission rate?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) await loadCommissionsForCollege(ratesCollege.id);
    } catch (_) {}
  };

  // Inline edit for an existing commission — updates value/type without
  // deleting-and-recreating the row. PATCH endpoint accepts a JSON body.
  const [editingCommissionId, setEditingCommissionId] = useState('');
  const [editCommissionDraft, setEditCommissionDraft] = useState({ commission_type: 'flat', commission_value: '' });

  const startEditCommission = (c) => {
    setEditingCommissionId(c.id);
    setEditCommissionDraft({
      commission_type: c.commission_type,
      commission_value: String(c.commission_value ?? ''),
    });
  };

  const cancelEditCommission = () => {
    setEditingCommissionId('');
    setEditCommissionDraft({ commission_type: 'flat', commission_value: '' });
  };

  const saveEditCommission = async () => {
    const id = editingCommissionId;
    if (!id) return;
    const val = Number(editCommissionDraft.commission_value);
    if (!Number.isFinite(val) || val < 0) {
      alert('Amount must be a non-negative number.');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commission_type: editCommissionDraft.commission_type,
          commission_value: val,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      cancelEditCommission();
      if (ratesCollege) await loadCommissionsForCollege(ratesCollege.id);
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [collegesRes, recruitersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/recruiters`, { credentials: 'include' })
      ]);

      if (collegesRes.ok) {
        const data = await collegesRes.json();
        setColleges(data.colleges || []);
      }

      if (recruitersRes.ok) {
        const data = await recruitersRes.json();
        setRecruiters(data.recruiters || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgram = () => {
    setProgramError('');
    const missing = [];
    if (!programForm.program_name.trim()) missing.push('Program Name');
    if (!String(programForm.tuition_fee).trim()) missing.push('Tuition Fee');
    if (!programForm.duration.trim()) missing.push('Duration');
    if (missing.length) {
      setProgramError(`Please fill: ${missing.join(', ')}.`);
      return;
    }
    // Coerce numeric fields to numbers (or null when empty)
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const programPayload = {
      program_name: programForm.program_name.trim(),
      tuition_fee: Number(programForm.tuition_fee),
      duration: programForm.duration.trim(),
      currency: programForm.currency || 'INR',
      college_to_tge_rate: num(programForm.college_to_tge_rate),
      college_to_tge_percent: num(programForm.college_to_tge_percent),
      tge_to_recruiter_rate: num(programForm.tge_to_recruiter_rate),
      tge_to_recruiter_percent: num(programForm.tge_to_recruiter_percent),
    };
    setCollegeForm({
      ...collegeForm,
      programs: [...collegeForm.programs, programPayload]
    });
    setProgramForm({
      program_name: '', tuition_fee: '', duration: '', currency: 'INR',
      college_to_tge_rate: '', college_to_tge_percent: '',
      tge_to_recruiter_rate: '', tge_to_recruiter_percent: '',
    });
  };

  const handleScrapePrograms = async () => {
    setScrapeError('');
    setScrapeResults([]);
    const url = scrapeUrl.trim();
    if (!url) { setScrapeError('Paste the URL of the college\'s programs page first.'); return; }
    setScrapeLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges/scrape-programs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ url }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'AI could not extract programs from that URL.');
      const programs = (data.programs || []).map((p) => ({ ...p, _selected: true }));
      if (programs.length === 0) {
        setScrapeError('AI did not find any programs on that page. Try a more specific URL like /programs or /courses.');
      } else {
        setScrapeResults(programs);
      }
    } catch (err) {
      setScrapeError(err.message);
    } finally {
      setScrapeLoading(false);
    }
  };

  const handleAddScrapedPrograms = () => {
    const picked = scrapeResults.filter((p) => p._selected && p.program_name.trim());
    if (!picked.length) { setScrapeError('Select at least one program to add.'); return; }
    const newPrograms = picked.map((p) => ({
      program_name: p.program_name.trim(),
      tuition_fee: 0,
      duration: (p.duration || '').trim() || 'TBD',
      currency: 'INR',
      college_to_tge_rate: null,
      college_to_tge_percent: null,
      tge_to_recruiter_rate: null,
      tge_to_recruiter_percent: null,
    }));
    setCollegeForm({ ...collegeForm, programs: [...collegeForm.programs, ...newPrograms] });
    setScrapeResults([]);
    setScrapeUrl('');
    setScrapeError('');
  };

  const handleCreateCollege = async (e) => {
    e.preventDefault();
    const isEdit = Boolean(editingCollege?.id);
    try {
      const url = isEdit
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges/${editingCollege.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(collegeForm),
      });

      if (response.ok) {
        alert(isEdit ? 'College updated successfully!' : 'College created successfully!');
        setShowAddModal(false);
        setEditingCollege(null);
        setCollegeForm({ name: '', authorized_contact_name: '', authorized_contact_email: '', province: '', city: '', programs: [] });
        fetchData();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`Error: ${data.detail || 'Could not save college'}`);
      }
    } catch (err) {
      alert(`Error ${isEdit ? 'updating' : 'creating'} college`);
    }
  };

  const handleOpenEdit = (college) => {
    setEditingCollege(college);
    setCollegeForm({
      name: college.name || '',
      authorized_contact_name: college.authorized_contact_name || '',
      authorized_contact_email: college.authorized_contact_email || '',
      province: college.province || '',
      city: college.city || '',
      programs: (college.programs || []).map((p) => ({
        program_name: p.program_name || '',
        tuition_fee: Number(p.tuition_fee || 0),
        duration: p.duration || '',
        currency: p.currency || 'INR',
        college_to_tge_rate: p.college_to_tge_rate ?? null,
        college_to_tge_percent: p.college_to_tge_percent ?? null,
        tge_to_recruiter_rate: p.tge_to_recruiter_rate ?? null,
        tge_to_recruiter_percent: p.tge_to_recruiter_percent ?? null,
      })),
    });
    setProgramError('');
    setEditingProgramIdx(-1);
    setScrapeUrl('');
    setScrapeError('');
    setScrapeResults([]);
    setShowAddModal(true);
  };

  const handleDeleteCollege = async (college) => {
    if (!window.confirm(`Delete ${college.name}? This will also remove all per-recruiter commission overrides for this college. This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/colleges/${college.id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(`Deleted${data.commissions_deleted ? ` (also removed ${data.commissions_deleted} commission overrides)` : ''}.`);
        fetchData();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(`Could not delete: ${data.detail || res.status}`);
      }
    } catch (err) {
      alert('Could not delete college.');
    }
  };

  // Update one field of one program in the in-form list (for inline edit).
  const updateProgramField = (idx, field, value) => {
    const numericFields = new Set([
      'tuition_fee', 'college_to_tge_rate', 'college_to_tge_percent',
      'tge_to_recruiter_rate', 'tge_to_recruiter_percent',
    ]);
    const next = collegeForm.programs.map((p, i) => {
      if (i !== idx) return p;
      let v = value;
      if (numericFields.has(field)) {
        v = (value === '' || value === null) ? null : Number(value);
      }
      return { ...p, [field]: v };
    });
    setCollegeForm({ ...collegeForm, programs: next });
  };

  const handleSetCommission = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/commissions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...commissionForm,
            college_id: selectedCollege.id
          })
        }
      );

      if (response.ok) {
        alert('Commission set successfully!');
        setShowCommissionModal(false);
        setCommissionForm({ recruiter_id: '', program_name: '', commission_type: 'percentage', commission_value: '' });
      }
    } catch (err) {
      alert('Error setting commission');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading colleges...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-serif-display text-3xl text-tge-navy">College Management</h2>
        <Dialog open={showAddModal} onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) {
            setEditingCollege(null);
            setCollegeForm({ name: '', authorized_contact_name: '', authorized_contact_email: '', province: '', city: '', programs: [] });
            setProgramError('');
            setEditingProgramIdx(-1);
            setScrapeUrl(''); setScrapeError(''); setScrapeResults([]);
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="college-add-btn">+ Add College</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCollege ? `Edit ${editingCollege.name}` : 'Add New College'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCollege} className="space-y-4">
              <div className="space-y-2">
                <Label>College Name *</Label>
                <Input
                  required
                  value={collegeForm.name}
                  onChange={(e) => setCollegeForm({ ...collegeForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Authorized Contact Name *</Label>
                <Input
                  required
                  value={collegeForm.authorized_contact_name}
                  onChange={(e) => setCollegeForm({ ...collegeForm, authorized_contact_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Authorized Contact Email *</Label>
                <Input
                  type="email"
                  required
                  value={collegeForm.authorized_contact_email}
                  onChange={(e) => setCollegeForm({ ...collegeForm, authorized_contact_email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Province / State</Label>
                  <Input
                    value={collegeForm.province}
                    onChange={(e) => setCollegeForm({ ...collegeForm, province: e.target.value })}
                    placeholder="e.g. British Columbia"
                    data-testid="college-province"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={collegeForm.city}
                    onChange={(e) => setCollegeForm({ ...collegeForm, city: e.target.value })}
                    placeholder="e.g. Vancouver"
                    data-testid="college-city"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-1">Add Programs</h4>
                <p className="text-xs text-gray-500 mb-3">For each program you can set how the college pays True Global, and how True Global pays the recruiter — either as a flat amount per enrolled student, a % of tuition, or both.</p>

                {/* AI scraper — paste a college webpage URL and Gemini extracts programs */}
                <div className="mb-4 rounded-xl border border-tge-navy/15 bg-tge-cream-soft/40 p-3" data-testid="program-scraper-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-tge-navy text-white text-[11px] font-bold">AI</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-tge-navy">Auto-fill from a webpage</div>
                      <div className="text-[11px] text-gray-500">Paste the college's "Programs" or "Courses" page URL and we'll extract program names + durations.</div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="url"
                      placeholder="https://college.edu/programs"
                      value={scrapeUrl}
                      onChange={(e) => { setScrapeUrl(e.target.value); setScrapeError(''); }}
                      data-testid="program-scraper-url"
                      disabled={scrapeLoading}
                    />
                    <Button
                      type="button"
                      onClick={handleScrapePrograms}
                      disabled={scrapeLoading || !scrapeUrl.trim()}
                      className="bg-tge-navy hover:bg-tge-navy-deep text-white shrink-0"
                      data-testid="program-scraper-fetch"
                    >
                      {scrapeLoading ? 'Scanning…' : 'Scan with AI'}
                    </Button>
                  </div>
                  {scrapeError && (
                    <div className="mt-2 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded-lg text-xs" data-testid="program-scraper-error">
                      {scrapeError}
                    </div>
                  )}
                  {scrapeResults.length > 0 && (
                    <div className="mt-3 bg-white border border-tge-cream rounded-lg" data-testid="program-scraper-results">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-tge-cream">
                        <div className="text-xs font-semibold text-tge-navy">{scrapeResults.filter((p) => p._selected).length} of {scrapeResults.length} selected</div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="text-[11px] text-tge-navy underline decoration-tge-gold/60 underline-offset-2"
                            onClick={() => setScrapeResults(scrapeResults.map((p) => ({ ...p, _selected: true })))}
                            data-testid="program-scraper-select-all"
                          >Select all</button>
                          <button
                            type="button"
                            className="text-[11px] text-gray-500 underline underline-offset-2"
                            onClick={() => setScrapeResults([])}
                            data-testid="program-scraper-clear"
                          >Clear</button>
                        </div>
                      </div>
                      <div className="max-h-56 overflow-y-auto divide-y divide-tge-cream/60">
                        {scrapeResults.map((p, idx) => (
                          <div key={idx} className="flex items-start gap-2 px-3 py-2">
                            <input
                              type="checkbox"
                              className="mt-1.5 accent-tge-navy"
                              checked={p._selected}
                              onChange={(e) => {
                                const next = [...scrapeResults];
                                next[idx] = { ...next[idx], _selected: e.target.checked };
                                setScrapeResults(next);
                              }}
                              data-testid={`program-scraper-toggle-${idx}`}
                            />
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
                              <Input
                                value={p.program_name}
                                onChange={(e) => {
                                  const next = [...scrapeResults];
                                  next[idx] = { ...next[idx], program_name: e.target.value };
                                  setScrapeResults(next);
                                }}
                                className="h-8 text-sm"
                                data-testid={`program-scraper-name-${idx}`}
                              />
                              <Input
                                value={p.duration}
                                placeholder="duration"
                                onChange={(e) => {
                                  const next = [...scrapeResults];
                                  next[idx] = { ...next[idx], duration: e.target.value };
                                  setScrapeResults(next);
                                }}
                                className="h-8 text-sm"
                                data-testid={`program-scraper-duration-${idx}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="px-3 py-2 border-t border-tge-cream flex items-center justify-between gap-2">
                        <div className="text-[11px] text-gray-500">Tuition + commission can be set after adding.</div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddScrapedPrograms}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="program-scraper-add-selected"
                        >
                          Add selected
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <div>
                    <Label className="text-[11px] text-gray-500">Program Name *</Label>
                    <Input
                      placeholder="e.g. MBA in Marketing"
                      value={programForm.program_name}
                      onChange={(e) => setProgramForm({ ...programForm, program_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-gray-500">Tuition Fee (per year) *</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g. 250000"
                      value={programForm.tuition_fee}
                      onChange={(e) => setProgramForm({ ...programForm, tuition_fee: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-gray-500">Duration *</Label>
                    <Input
                      placeholder="e.g. 2 years"
                      value={programForm.duration}
                      onChange={(e) => setProgramForm({ ...programForm, duration: e.target.value })}
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                  <div>
                    <Label className="text-[11px] text-gray-500">Currency</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={programForm.currency}
                      onChange={(e) => setProgramForm({ ...programForm, currency: e.target.value })}
                    >
                      <option value="INR">INR ₹</option>
                      <option value="USD">USD $</option>
                      <option value="GBP">GBP £</option>
                      <option value="EUR">EUR €</option>
                      <option value="CAD">CAD C$</option>
                      <option value="AUD">AUD A$</option>
                    </select>
                  </div>
                </div>

                {/* College → True Global */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mb-2">
                    College pays True Global Education
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-gray-500">Flat amount per student</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g. 50000"
                        value={programForm.college_to_tge_rate}
                        onChange={(e) => setProgramForm({ ...programForm, college_to_tge_rate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-gray-500">— OR — % of tuition</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="e.g. 12"
                        value={programForm.college_to_tge_percent}
                        onChange={(e) => setProgramForm({ ...programForm, college_to_tge_percent: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* True Global → Recruiter */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-2">
                    True Global pays recruiter
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] text-gray-500">Flat amount per student</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g. 25000"
                        value={programForm.tge_to_recruiter_rate}
                        onChange={(e) => setProgramForm({ ...programForm, tge_to_recruiter_rate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-gray-500">— OR — % of tuition</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="e.g. 6"
                        value={programForm.tge_to_recruiter_percent}
                        onChange={(e) => setProgramForm({ ...programForm, tge_to_recruiter_percent: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {programError && (
                  <div className="mb-2 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded-lg text-xs" data-testid="program-form-error">
                    {programError}
                  </div>
                )}

                <Button type="button" variant="outline" size="sm" onClick={handleAddProgram} data-testid="program-add-btn">
                  + Add Program
                </Button>
                {collegeForm.programs.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {collegeForm.programs.map((prog, idx) => {
                      const cur = prog.currency || 'INR';
                      const isEditing = editingProgramIdx === idx;
                      const collegeShare = prog.college_to_tge_rate
                        ? `${cur} ${Number(prog.college_to_tge_rate).toLocaleString()} flat`
                        : prog.college_to_tge_percent ? `${prog.college_to_tge_percent}% of tuition` : '—';
                      const recruiterShare = prog.tge_to_recruiter_rate
                        ? `${cur} ${Number(prog.tge_to_recruiter_rate).toLocaleString()} flat`
                        : prog.tge_to_recruiter_percent ? `${prog.tge_to_recruiter_percent}% of tuition` : '—';
                      if (isEditing) {
                        return (
                          <div key={idx} className="text-xs bg-tge-cream-soft border-2 border-tge-navy/30 p-3 rounded-lg" data-testid={`program-row-edit-${idx}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                              <div>
                                <Label className="text-[10px] text-gray-500">Program Name</Label>
                                <Input className="h-8 text-xs" value={prog.program_name || ''} onChange={(e) => updateProgramField(idx, 'program_name', e.target.value)} />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-500">Tuition Fee</Label>
                                <Input className="h-8 text-xs" type="number" min="0" value={prog.tuition_fee ?? 0} onChange={(e) => updateProgramField(idx, 'tuition_fee', e.target.value)} />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-500">Duration</Label>
                                <Input className="h-8 text-xs" value={prog.duration || ''} onChange={(e) => updateProgramField(idx, 'duration', e.target.value)} />
                              </div>
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-500">Currency</Label>
                              <select
                                className="h-8 w-full border rounded-md px-2 text-xs"
                                value={prog.currency || 'INR'}
                                onChange={(e) => updateProgramField(idx, 'currency', e.target.value)}
                              >
                                <option value="INR">INR ₹</option>
                                <option value="USD">USD $</option>
                                <option value="CAD">CAD $</option>
                                <option value="GBP">GBP £</option>
                                <option value="EUR">EUR €</option>
                                <option value="AUD">AUD $</option>
                              </select>
                            </div>
                            <div className="mt-2 p-2 rounded-md bg-emerald-50/60 border border-emerald-200/60">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-1">College → TGE</div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px] text-gray-500">Flat per student</Label>
                                  <Input className="h-8 text-xs" type="number" min="0" placeholder="e.g. 50000" value={prog.college_to_tge_rate ?? ''} onChange={(e) => updateProgramField(idx, 'college_to_tge_rate', e.target.value)} />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-gray-500">— OR — % of tuition</Label>
                                  <Input className="h-8 text-xs" type="number" min="0" max="100" step="0.1" placeholder="e.g. 12" value={prog.college_to_tge_percent ?? ''} onChange={(e) => updateProgramField(idx, 'college_to_tge_percent', e.target.value)} />
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 p-2 rounded-md bg-amber-50/60 border border-amber-200/60">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">TGE → Recruiter</div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px] text-gray-500">Flat per student</Label>
                                  <Input className="h-8 text-xs" type="number" min="0" placeholder="e.g. 25000" value={prog.tge_to_recruiter_rate ?? ''} onChange={(e) => updateProgramField(idx, 'tge_to_recruiter_rate', e.target.value)} />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-gray-500">— OR — % of tuition</Label>
                                  <Input className="h-8 text-xs" type="number" min="0" max="100" step="0.1" placeholder="e.g. 6" value={prog.tge_to_recruiter_percent ?? ''} onChange={(e) => updateProgramField(idx, 'tge_to_recruiter_percent', e.target.value)} />
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2 justify-end">
                              <button
                                type="button"
                                onClick={() => setEditingProgramIdx(-1)}
                                className="px-3 py-1 rounded-md bg-tge-navy text-white text-[11px] font-semibold hover:bg-tge-navy-deep"
                                data-testid={`program-row-done-${idx}`}
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className="text-xs bg-gray-50 border border-gray-200 p-3 rounded-lg flex items-start gap-2" data-testid={`program-row-${idx}`}>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{prog.program_name} <span className="text-gray-400 font-normal">· {prog.duration || '—'}</span></div>
                            <div className="text-gray-600 mt-0.5">Tuition: {cur} {Number(prog.tuition_fee || 0).toLocaleString()}</div>
                            <div className="flex gap-3 mt-1 flex-wrap">
                              <span><span className="text-emerald-700 font-medium">College → TGE:</span> {collegeShare}</span>
                              <span><span className="text-amber-700 font-medium">TGE → Recruiter:</span> {recruiterShare}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <button
                              type="button"
                              onClick={() => setEditingProgramIdx(idx)}
                              className="text-tge-navy hover:underline text-[10px] font-semibold"
                              data-testid={`program-row-edit-btn-${idx}`}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (editingProgramIdx === idx) setEditingProgramIdx(-1);
                                setCollegeForm({ ...collegeForm, programs: collegeForm.programs.filter((_, i) => i !== idx) });
                              }}
                              className="text-red-500 hover:text-red-700 text-[10px]"
                              data-testid={`program-row-remove-btn-${idx}`}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" data-testid="college-save-btn">{editingCollege ? 'Save changes' : 'Create College'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {colleges.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500">No colleges added yet.</p>
          </CardContent>
        </Card>
      ) : (
        <CollegeListView
          colleges={colleges}
          onEdit={handleOpenEdit}
          onDelete={handleDeleteCollege}
          onSetCommission={(college) => { setSelectedCollege(college); setShowCommissionModal(true); }}
          onOpenRates={openRatesModal}
        />
      )}

      {/* Set Commission dialog (per-recruiter override, hoisted out of the list) */}
      <Dialog open={showCommissionModal} onOpenChange={setShowCommissionModal}>
        <DialogContent data-testid="set-commission-dialog">
          <DialogHeader>
            <DialogTitle>Set Commission - {selectedCollege?.name}</DialogTitle>
            <DialogDescription className="sr-only">Set a per-recruiter commission for this college</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSetCommission} className="space-y-4">
            <div className="space-y-2">
              <Label>Select Recruiter</Label>
              <select
                required
                className="w-full border rounded-md p-2"
                value={commissionForm.recruiter_id}
                onChange={(e) => setCommissionForm({ ...commissionForm, recruiter_id: e.target.value })}
                data-testid="commission-recruiter-select"
              >
                <option value="">Choose recruiter...</option>
                {recruiters.map((r) => (
                  <option key={r.recruiter_id} value={r.recruiter_id}>
                    {r.company_name} ({r.recruiter_id})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Select Program</Label>
              <select
                required
                className="w-full border rounded-md p-2"
                value={commissionForm.program_name}
                onChange={(e) => setCommissionForm({ ...commissionForm, program_name: e.target.value })}
                data-testid="commission-program-select"
              >
                <option value="">Choose program...</option>
                {selectedCollege?.programs?.map((prog, idx) => (
                  <option key={idx} value={prog.program_name}>{prog.program_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Commission Type</Label>
              <select
                className="w-full border rounded-md p-2"
                value={commissionForm.commission_type}
                onChange={(e) => setCommissionForm({ ...commissionForm, commission_type: e.target.value })}
                data-testid="commission-type-select"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat Amount ($)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Commission Value</Label>
              <Input
                type="number"
                step="0.01"
                required
                value={commissionForm.commission_value}
                onChange={(e) => setCommissionForm({ ...commissionForm, commission_value: e.target.value })}
                placeholder={commissionForm.commission_type === 'percentage' ? '10' : '500'}
                data-testid="commission-value-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCommissionModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="commission-submit-btn">Set Commission</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Per-recruiter commission rates */}
      <Dialog open={showRatesModal} onOpenChange={setShowRatesModal}>
        <DialogContent className="max-w-2xl w-[94vw]" data-testid="recruiter-rates-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-tge-navy text-xl">
              Recruiter commission rates · {ratesCollege?.name}
            </DialogTitle>
            <DialogDescription>
              Override the default per-program rate for specific recruiters. Recruiters see their assigned rates in their own dashboard. Recruiters without an override use the default "TGE → Recruiter" rate set on the program.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitCommission} className="bg-tge-cream-soft border border-tge-navy/10 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-gray-500">Recruiter *</Label>
                <select
                  value={commissionForm.recruiter_id}
                  onChange={(e) => setCommissionForm({ ...commissionForm, recruiter_id: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                  data-testid="rates-recruiter-select"
                  required
                >
                  <option value="">Select recruiter...</option>
                  {recruiters.map((r) => (
                    <option key={r.recruiter_id} value={r.recruiter_id}>
                      {r.company_name} ({r.recruiter_id})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-gray-500">Program *</Label>
                <select
                  value={commissionForm.program_name}
                  onChange={(e) => setCommissionForm({ ...commissionForm, program_name: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                  data-testid="rates-program-select"
                  required
                >
                  {ratesCollege?.programs?.map((p, i) => (
                    <option key={i} value={p.program_name}>{p.program_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-gray-500">Type *</Label>
                <select
                  value={commissionForm.commission_type}
                  onChange={(e) => setCommissionForm({ ...commissionForm, commission_type: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                >
                  <option value="flat">Flat amount per student</option>
                  <option value="percentage">Percentage of tuition</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-gray-500">Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  placeholder={commissionForm.commission_type === 'percentage' ? 'e.g. 7.5' : 'e.g. 30000'}
                  value={commissionForm.commission_value}
                  onChange={(e) => setCommissionForm({ ...commissionForm, commission_value: e.target.value })}
                  data-testid="rates-amount-input"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-tge-navy hover:bg-tge-navy-deep text-white" data-testid="rates-submit-btn">
              Set / update rate
            </Button>
          </form>

          <div className="mt-2">
            <div className="text-xs uppercase tracking-wider text-tge-navy/60 font-semibold mb-2">
              Active recruiter rates ({collegeCommissions.length})
            </div>
            {collegeCommissions.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-6">No per-recruiter overrides yet. Recruiters use the default rate set on each program.</div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {collegeCommissions.map((c) => {
                  const isEditing = editingCommissionId === c.id;
                  return (
                    <div key={c.id} className="flex items-center justify-between text-xs bg-white border border-tge-navy/10 rounded-lg px-3 py-2" data-testid={`commission-row-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-tge-navy truncate">{c.recruiter_name}</div>
                        <div className="text-gray-500">{c.program_name}</div>
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-1.5 shrink-0 mr-2" data-testid={`commission-edit-${c.id}`}>
                          <select
                            value={editCommissionDraft.commission_type}
                            onChange={(e) => setEditCommissionDraft({ ...editCommissionDraft, commission_type: e.target.value })}
                            className="h-7 rounded-md border border-input bg-white px-2 text-[11px]"
                            data-testid={`commission-edit-type-${c.id}`}
                          >
                            <option value="flat">Flat</option>
                            <option value="percentage">%</option>
                          </select>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editCommissionDraft.commission_value}
                            onChange={(e) => setEditCommissionDraft({ ...editCommissionDraft, commission_value: e.target.value })}
                            className="h-7 w-24 text-[11px]"
                            data-testid={`commission-edit-value-${c.id}`}
                          />
                          <button
                            onClick={saveEditCommission}
                            className="text-emerald-600 hover:text-emerald-700 text-xs font-medium"
                            data-testid={`commission-edit-save-${c.id}`}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditCommission}
                            className="text-gray-500 hover:text-gray-700 text-xs"
                            data-testid={`commission-edit-cancel-${c.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="text-right shrink-0 mr-3">
                            <Badge className="bg-amber-100 text-amber-700 border-0">
                              {c.commission_type === 'flat' ? `${Number(c.commission_value).toLocaleString()} flat` : `${c.commission_value}%`}
                            </Badge>
                          </div>
                          <button
                            onClick={() => startEditCommission(c)}
                            className="text-tge-navy/70 hover:text-tge-navy text-xs mr-2"
                            data-testid={`commission-edit-btn-${c.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeCommission(c.id)}
                            className="text-rose-500 hover:text-rose-700 text-xs"
                            data-testid={`commission-remove-btn-${c.id}`}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollegeManagement;
